import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import AuthContext from "../Context/AuthContext";

export default function AccountUpdate(props) {
  const { loggedIn } = useContext(AuthContext);
  const history = useHistory();

  const [data, setData] = useState(null);

  const [accName, setAccName] = useState("");
  const [company, setCompany] = useState("");
  const [platform, setPlatform] = useState("");
  const [image, setImage] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [success, setSuccess] = useState(false);
  const [errorField, setErrorField] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [showConfirm, setShowConfirm] = useState(false);

  const id = props.match?.params?.id;

  // Fetch account
  useEffect(() => {
    let mounted = true;
    async function fetchAccount() {
      try {
        setIsLoading(true);
        const res = await axios.post("https://act-api.vercel.app/findaccount", { id });
        if (!mounted) return;
        setData(res.data || {});
        setAccName(res.data?.name || "");
        setCompany(res.data?.company || "");
        setPlatform(res.data?.platform || "");
        setImage(res.data?.imageURL || "");
        setErrorMsg("");
      } catch (e) {
        if (!mounted) return;
        setErrorMsg("Account bilgileri yüklenemedi. Lütfen tekrar deneyin.");
      } finally {
        mounted && setIsLoading(false);
      }
    }
    if (id) fetchAccount();
    return () => {
      mounted = false;
    };
  }, [id]);

  // Basic client validation
  function validate() {
    if (!accName?.trim()) return "Account Name is required.";
    if (!company?.trim()) return "Company Name is required.";
    if (!platform?.trim()) return "Platform is required.";
    return "";
  }

  async function submit(e) {
    e.preventDefault();
    setSuccess(false);
    setErrorField("");
    setErrorMsg("");

    const v = validate();
    if (v) {
      setErrorField(v);
      return;
    }

    try {
      setIsSaving(true);
      const response = await axios.post("https://act-api.vercel.app/accountupdate", {
        id,
        accName,
        company,
        platform,
        image,
      });
      if (response.data?.success) {
        setSuccess(true);
      } else {
        setErrorMsg("Güncelleme sırasında beklenmeyen bir hata oluştu.");
      }
    } catch (error) {
      const apiMsg =
        error?.response?.data?.ErrorType === "Field"
          ? error?.response?.data?.ErrorMessage
          : "Güncelleme başarısız oldu.";
      setErrorField(apiMsg || "");
      setErrorMsg(
        error?.response?.data?.ErrorType && error?.response?.data?.ErrorType !== "Field"
          ? error?.response?.data?.ErrorMessage
          : ""
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function removeAccount() {
    try {
      setIsDeleting(true);
      await axios.post("https://act-api.vercel.app/removeaccount", { id });
      history.push("/accounts");
    } catch (e) {
      setIsDeleting(false);
      setErrorMsg("Hesap silinemedi. Lütfen tekrar deneyin.");
      setShowConfirm(false);
    }
  }

  // UI helpers
  function ImagePreview() {
    const [broken, setBroken] = useState(false);
    const displayInitials =
      !image?.trim() || broken || image.startsWith("data:image/svg+xml");

    return (
      <div className="w-20 h-20 rounded-full ring-2 ring-slate-100 bg-slate-100 overflow-hidden flex items-center justify-center">
        {displayInitials ? (
          <span className="text-slate-500 font-semibold text-lg">
            {(accName?.[0] || "A").toUpperCase()}
          </span>
        ) : (
          <img
            src={image}
            alt={accName || "Account"}
            className="w-full h-full object-cover"
            onError={() => setBroken(true)}
          />
        )}
      </div>
    );
  }

  if (loggedIn === false) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-b from-slate-50 to-slate-100">
        <div className="text-center">
          <p className="text-slate-700 font-semibold text-lg">You are not logged in.</p>
          <p className="text-slate-500 mt-2">
            Go to{" "}
            <a href="/login" className="text-blue-600 underline">
              Login
            </a>{" "}
            page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-800">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/50 sticky top-0 z-10">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-3 flex items-center justify-between">
          <button
            onClick={() => history.goBack()}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <span className="text-sm text-slate-500">Account Update</span>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
        {errorMsg && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {errorMsg}
          </div>
        )}
        {success && (
          <div className="mb-6 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
            You have successfully updated the information.
          </div>
        )}

        {isLoading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-slate-200 animate-pulse" />
              <div className="flex-1">
                <div className="h-4 w-2/3 bg-slate-200 rounded animate-pulse mb-2" />
                <div className="h-3 w-1/3 bg-slate-200 rounded animate-pulse" />
              </div>
            </div>
            <div className="mt-6 space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-10 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </div>
        ) : data ? (
          <form
            onSubmit={submit}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            {/* Top */}
            <div className="flex items-center gap-4">
              <ImagePreview />
              <div className="min-w-0">
                <h1 className="text-lg font-semibold truncate">
                  {data?.name || "Account"}
                </h1>
                <p className="text-xs text-slate-500 truncate">
                  {data?.company || "—"} • {data?.platform || "—"}
                </p>
              </div>
            </div>

            {/* Form fields */}
            <div className="mt-6 grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Account Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                  placeholder="Enter account name"
                  value={accName}
                  onChange={(e) => setAccName(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                  placeholder="Enter company name"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Platform <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                  placeholder="Meta / Google / TikTok …"
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Image URL
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400"
                  placeholder="https://…"
                  value={image}
                  onChange={(e) => setImage(e.target.value)}
                />
                
              </div>

              {errorField && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {errorField}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <svg
                      className="h-4 w-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                    Saving…
                  </>
                ) : (
                  "Update"
                )}
              </button>

              <button
                type="button"
                onClick={() => setShowConfirm(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 focus:outline-none focus:ring-4 focus:ring-red-100"
              >
                Remove Account
              </button>

              <button
                type="button"
                onClick={() => history.push("/accounts")}
                className="ml-auto inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-600">Hesap bulunamadı.</p>
          </div>
        )}
      </div>

      {/* Delete confirm modal */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowConfirm(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold">Remove account?</h3>
            <p className="mt-2 text-sm text-slate-600">
              Bu hesabı silmek geri alınamaz. Emin misiniz?
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={removeAccount}
                disabled={isDeleting}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isDeleting ? "Removing…" : "Yes, remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
